/* cart-drawer-fix.js (v3 - FIXED DOUBLE-ADD)
   Opens cart drawer after add-to-cart.
   Updates the drawer AFTER Shopify finishes cart/change requests.
   FIXED: No longer adds items twice.
*/

(() => {
  const ROOT =
    (window.Shopify && Shopify.routes && Shopify.routes.root)
      ? Shopify.routes.root
      : '/';

  const qs = (sel, root = document) => root.querySelector(sel);

  function findDrawer(root = document) {
    return (
      qs('cart-drawer', root) ||
      qs('#CartDrawer', root) ||
      qs('.cart-drawer', root) ||
      qs('[data-cart-drawer]', root) ||
      qs('[data-drawer="cart"]', root)
    );
  }

  function isDrawerOpen(drawer) {
    if (!drawer) return false;
    if (drawer.classList.contains('active')) return true;
    if (drawer.classList.contains('is-open')) return true;
    if (drawer.hasAttribute('open')) return true;
    if (drawer.getAttribute('aria-hidden') === 'false') return true;
    return false;
  }

  function openDrawer(force = true) {
    const drawer = findDrawer();
    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (typeof drawer.show === 'function') {
      drawer.show();
      return;
    }

    if (!force && isDrawerOpen(drawer)) return;

    drawer.classList.add('active', 'is-open', 'open');
    drawer.setAttribute('open', 'true');
    drawer.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
  }

  function forceUnlockScroll() {
    const html = document.documentElement;
    const body = document.body;
    
    html.classList.remove('overflow-hidden', 'no-scroll', 'scroll-lock', 'modal-open', 'drawer-open');
    body.classList.remove('overflow-hidden', 'no-scroll', 'scroll-lock', 'modal-open', 'drawer-open');
    
    if (html.style.overflow === 'hidden') html.style.overflow = '';
    if (body.style.overflow === 'hidden') body.style.overflow = '';
    if (html.style.position === 'fixed') html.style.position = '';
    if (body.style.position === 'fixed') body.style.position = '';
  }

  async function renderSections(handles) {
    const url = `${ROOT}?sections=${encodeURIComponent(handles.join(','))}&_=${Date.now()}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return null;
    return await res.json();
  }

  function replaceDrawerFromHtml(html, keepOpen) {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const newDrawer = findDrawer(temp);
    const currentDrawer = findDrawer(document);

    if (newDrawer && currentDrawer) {
      const wasOpen = isDrawerOpen(currentDrawer);
      
      if (keepOpen || wasOpen) {
        newDrawer.classList.add('active', 'is-open', 'open');
        newDrawer.setAttribute('open', 'true');
        newDrawer.setAttribute('aria-hidden', 'false');
      }
      
      currentDrawer.replaceWith(newDrawer);
      
      if (keepOpen || wasOpen) {
        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');
      }
    }
  }

  let refreshTimer = null;
  let refreshInFlight = false;
  let refreshQueued = false;
  let queuedKeepOpen = false;
  let queuedForceOpen = false;

  async function doRefresh() {
    if (refreshInFlight) {
      refreshQueued = true;
      return;
    }

    refreshInFlight = true;

    const keepOpen = queuedKeepOpen;
    const forceOpen = queuedForceOpen;

    refreshQueued = false;
    queuedKeepOpen = false;
    queuedForceOpen = false;

    try {
      const sections = await renderSections(['cart-drawer']);
      if (sections && sections['cart-drawer']) {
        replaceDrawerFromHtml(sections['cart-drawer'], keepOpen || forceOpen);
      } else if (forceOpen) {
        openDrawer(true);
      }
    } catch (e) {
      if (forceOpen) openDrawer(true);
    } finally {
      refreshInFlight = false;
      if (refreshQueued) doRefresh();
    }
  }

  function scheduleRefresh({ keepOpen = false, forceOpen = false } = {}) {
    queuedKeepOpen = queuedKeepOpen || keepOpen;
    queuedForceOpen = queuedForceOpen || forceOpen;

    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      doRefresh();
    }, 200);
  }

  // FIXED: Only wrap fetch, DON'T intercept form submits
  // Let the theme handle the actual add-to-cart, we just listen and open drawer
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const input = args[0];
    const init = args[1] || {};

    const headers = init.headers || {};
    const hasFixHeader =
      (headers && (
        headers['X-CartDrawerFix'] ||
        (typeof headers.get === 'function' && headers.get('X-CartDrawerFix'))
      ));

    const url = typeof input === 'string' ? input : (input && input.url) ? input.url : '';

    const isSectionsReq = url.includes('sections=');
    const isCartEndpoint =
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear');

    const isAdd = url.includes('/cart/add');
    const isChangeLike =
      url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear');

    const res = await originalFetch(...args);

    // Only react AFTER successful cart operations
    if (!hasFixHeader && !isSectionsReq && isCartEndpoint && res && res.ok) {
      const drawer = findDrawer();
      const keepOpen = drawer ? isDrawerOpen(drawer) : false;

      if (isAdd) {
        scheduleRefresh({ keepOpen: true, forceOpen: true });
      } else if (isChangeLike) {
        scheduleRefresh({ keepOpen });
      }
    }

    return res;
  };

  // REMOVED: Form submit interception (this was causing double-add)
  // The theme already handles add-to-cart via fetch, so we don't need to intercept forms

  // Scroll unlock checker
  setInterval(() => {
    const drawer = findDrawer();
    if (!drawer || !isDrawerOpen(drawer)) {
      forceUnlockScroll();
    }
  }, 100);
})();